/**
 * Date helper utilities for parsing and formatting event dates
 */

/**
 * Parse a date string from the API (handles both MySQL and ISO formats)
 * @param {string} dateString - Date string from API
 * @returns {Date} JavaScript Date object
 */
export function parseEventDate(dateString, timeZone = 'UTC') {
  if (!dateString) return null;

  const normalized = dateString.includes('T')
    ? dateString
    : dateString.replace(' ', 'T');

  const hasExplicitOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);

  if (!hasExplicitOffset && typeof dayjs?.tz === 'function' && timeZone) {
    try {
      const zoned = dayjs.tz(normalized, timeZone);
      if (zoned.isValid()) {
        return zoned.toDate();
      }
    } catch (_err) {
      // dayjs.tz can throw "Invalid time value" - fall through to plain dayjs
    }
  }

  const fallback = dayjs(normalized);
  return fallback.isValid() ? fallback.toDate() : null;
}

/**
 * Format event date for display
 * @param {Date} date - Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatEventDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return 'Invalid Date';
  }
  // ll	MMM D, YYYY	Aug 16, 2018
  return dayjs.utc(date).local().format('ddd, MMM D, YYYY');
}

/**
 * Format event time for display
 * @param {Date} date - Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time string
 */
export function formatEventTime(date) {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return 'Invalid Time';
  }

  return dayjs.utc(date).local().format('h:mm A');
}

/**
 * Format a Date object for datetime-local input (YYYY-MM-DDTHH:MM)
 * Converts UTC date to local time for display in the input field
 * @param {Date} date - Date object (assumed to be in UTC from API)
 * @returns {string} Formatted string for datetime-local input
 */
export function formatDateTimeForInput(date, timeZone) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  try {
    if (typeof dayjs?.tz === 'function' && timeZone) {
      return dayjs(date).tz(timeZone).format('YYYY-MM-DDTHH:mm');
    }
    return dayjs(date).format('YYYY-MM-DDTHH:mm');
  } catch (_err) {
    return '';
  }
}

/**
 * Regex for datetime-local format: YYYY-MM-DD[ T]HH:mm or YYYY-MM-DD[ T]HH:mm:ss
 * Used to validate before parsing to avoid dayjs.tz throwing on malformed input
 */
const DATETIME_LOCAL_REGEX = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2})?$/;

/**
 * Convert datetime-local string (user's local time) to MySQL DATETIME format without timezone conversion
 * @param {string} datetimeLocal - Datetime in format "YYYY-MM-DDTHH:MM" (local time)
 * @param {string} [timeZone] - IANA timezone identifier to interpret the value in
 * @returns {string|null} MySQL DATETIME format "YYYY-MM-DD HH:MM:SS" or null if invalid
 */
export function formatLocalDateTimeToMySQL(datetimeLocal, timeZone) {
  const trimmed = typeof datetimeLocal === 'string' ? datetimeLocal.trim() : '';
  if (!trimmed) {
    return null;
  }

  // Validate format before parsing - dayjs.tz can throw "Invalid time value" for
  // malformed input (via Intl.DateTimeFormat.formatToParts)
  if (!DATETIME_LOCAL_REGEX.test(trimmed)) {
    return null;
  }

  let localDate;
  try {
    if (timeZone && typeof dayjs?.tz === 'function') {
      localDate = dayjs.tz(trimmed, timeZone);
    } else {
      localDate = dayjs(trimmed);
    }
  } catch (_err) {
    return null;
  }

  if (!localDate.isValid()) {
    return null;
  }

  // Format as MySQL DATETIME (YYYY-MM-DD HH:MM:SS)
  return localDate.format('YYYY-MM-DD HH:mm:ss');
}
